import type { FunctionComponent, HTMLProps } from 'react'
import React from 'react'

import classnames from 'classnames'

import type { SocialIconName } from '../Links/SocialLink'
import { SocialLink } from '../Links/SocialLink'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './Share.module.scss'
import { shareData } from './shareData'

export const Share: FunctionComponent<Props> = ({
  links,
  headline,
  orientation = 'horizontal',
  className,
  location,
  ...rest
}) => {
  const linksTypes = Object.keys(links).filter(
    (linkType) => links[linkType as LinkTypes]
  )

  const isHorizontal = orientation === 'horizontal'

  return (
    <div
      className={classnames(styles.shareWrapper, className)}
      data-testid="share-wrapper"
      {...rest}
    >
      {isHorizontal && headline && (
        <TypographyV2
          tag="span"
          textStyle="sb-text-interface-body-small-regular"
          className={styles.shareHeadlineText}
          data-testid="share-headline"
        >
          {headline}
        </TypographyV2>
      )}
      <ul
        className={classnames(
          styles.shareLinksList,
          isHorizontal ? styles.shareHorizontal : styles.shareVertical
        )}
        data-testid={`sharing-${orientation}`}
      >
        {linksTypes.map((linkType) => {
          const icon = shareData[linkType as LinkTypes]

          if (!icon) {
            return null
          }

          return (
            <li
              className={classnames(
                styles.shareListItem,
                !isHorizontal && styles.shareListItemVertical
              )}
              data-testid="share-list-item"
              key={linkType}
            >
              <SocialLink
                href={links[linkType as LinkTypes] || ''}
                icon={linkType as SocialIconName}
                aria-label={icon.ariaDesc || ''}
                data-testid={linkType}
                data-which-id={`social-share-${linkType}-${location}`}
              >
                {!isHorizontal && icon.mainDesc}
              </SocialLink>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Links that contains a list of share option urls.
   */
  links: ShareSocialLinks
  /**
   Location on page, used for data tracking
   */
  location: Location
  /**
   * Determines the direction of the share links
   */
  orientation?: Orientation
  /**
   * Class name that can be added to apply additional styling
   */
  className?: string
  /**
   * Headline text that will be displayed instead of the default headline text.
   */
  headline?: string
} & HTMLProps<HTMLDivElement>

type ShareSocialLinks = {
  facebook?: string
  twitter?: string
  whatsapp?: string
  mail?: string
  print?: string
  download?: string
  x?: string
}

export type Orientation = 'horizontal' | 'vertical'

export type LinkTypes = keyof ShareSocialLinks

export type Location = 'top' | 'bottom'
